// deps
import React, { memo, useMemo } from "react";

// internal-deps
import { getCloudinaryUrl, LottieAnimation, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";

// constants
import {
    SPLIT_BILL_LOTTIE_PROPS,
} from "@dineout-payment-success-ui/constants";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";

// interfaces
import type { ISplitPayWidget } from "@dineout-payment-success-ui/interfaces";

// utils
import {
    getCurrentSplitWidgetStateFromApi,
    getSplitBillWidgetViewState,
    SplitBillWidgetTitleBadgeType,
    type SplitBillWidgetTitleBadge,
} from "./splitBillWidgetUtils";

// styles
import {
    SplitBillWrapper,
    SplitBillContentWrapper,
    SplitBillTitleWrapper,
    SplitBillSubTitleWrapper,
    SplitBillImageContainer,
    SplitBillCta,
    SplitBillCtaIcon,
    SplitBillSuccessIconWrap,
} from "./styles";

interface SplitBillWidgetProps {
    splitBillSuccess?: boolean;
    currentSplitWidgetState?: string;
    splitPayWidget?: ISplitPayWidget;
    onClick: VoidFunction;
    onImpression?: VoidFunction;
}

const SplitBillTitleBadgeView = memo<{ badge: SplitBillWidgetTitleBadge }>(({ badge }) => {
    if (badge.type === SplitBillWidgetTitleBadgeType.IMAGE) {
        return (
            <Image
                cloudinaryId={badge.cloudinaryId}
                width={badge.width}
                height={badge.height}
                data-testid={SPLIT_BILL_TEST_IDS.TITLE_BADGE}
            />
        );
    }

    return (
        <SplitBillSuccessIconWrap data-testid="success-icon-wrap">
            <SpriteIconWeb
                iconName={badge.iconName}
                fillColor={badge.fillColor}
                strokeColor={badge.strokeColor}
            />
        </SplitBillSuccessIconWrap>
    );
});

const SplitBillWidget: React.FC<SplitBillWidgetProps> = ({ splitBillSuccess, currentSplitWidgetState, splitPayWidget, onClick, onImpression }) => {

    const getCurrentStateFromApi = getCurrentSplitWidgetStateFromApi(splitPayWidget);

    const widgetViewState = useMemo(
        () =>
            !isEmpty(splitPayWidget) && splitPayWidget?.states
                ? getSplitBillWidgetViewState(
                      getCurrentStateFromApi,
                      splitPayWidget,
                      splitBillSuccess,
                      currentSplitWidgetState,
                  )
                : null,
        [getCurrentStateFromApi, splitBillSuccess, currentSplitWidgetState, splitPayWidget],
    );

    const lottieId = useMemo(() => {
        if (!splitPayWidget?.states) {
            return "";
        }

        const lottieStateKey = widgetViewState?.lottieId;
        return lottieStateKey || "";
    }, [splitPayWidget]);

    useEffectOnce(() => {
        if (!isEmpty(splitPayWidget)) {
            onImpression?.();
        }
    });

    if (isEmpty(splitPayWidget)) {
        return null;
    }

    const lottieAnimation = (
        <SplitBillImageContainer>
            <LottieAnimation
                {...SPLIT_BILL_LOTTIE_PROPS}
                fileName={getCloudinaryUrl(lottieId)}
                visible={true}
                playLottie
            />
        </SplitBillImageContainer>
    );

    return (
        <SplitBillWrapper
            backgroundColor={splitPayWidget.backgroundColor}
            isClickable={widgetViewState?.isClickable}
            onClick={widgetViewState?.isClickable ? onClick : undefined}
            data-testid={SPLIT_BILL_TEST_IDS.WIDGET}
        >
            <SplitBillContentWrapper>
                {!isEmpty(widgetViewState) && (
                    <>
                        <SplitBillTitleWrapper>
                            {widgetViewState.titleBadge?.placement === "beforeTitle" && (
                                <SplitBillTitleBadgeView badge={widgetViewState.titleBadge} />
                            )}
                            <Text variant="V2.body2.neutral.bold" color="text_High_Emphasis">
                                {widgetViewState.title}
                            </Text>
                            {widgetViewState.titleBadge?.placement === "afterTitle" && (
                                <SplitBillTitleBadgeView badge={widgetViewState.titleBadge} />
                            )}
                        </SplitBillTitleWrapper>
                        <SplitBillSubTitleWrapper isSuccessState={widgetViewState.useSuccessSubtitleWidth}>
                            <Text variant="V2.overline.neutral.medium" color="text_Low_Emphasis">
                                {widgetViewState.subTitle}
                            </Text>
                        </SplitBillSubTitleWrapper>
                        {widgetViewState.cta && (
                            <SplitBillCta data-testid={SPLIT_BILL_TEST_IDS.CTA}>
                                <Text variant="V2.body2.neutral.bold" color="primary">
                                    {widgetViewState.cta.text}
                                </Text>
                                <SplitBillCtaIcon data-testid="cta-chevron-icon">
                                    <SpriteIconWeb
                                        iconName={widgetViewState.cta.iconName}
                                        fillColor={widgetViewState.cta.fillColor}
                                        strokeColor={widgetViewState.cta.strokeColor}
                                    />
                                </SplitBillCtaIcon>
                            </SplitBillCta>
                        )}
                    </>
                )}
            </SplitBillContentWrapper>
            {lottieAnimation}
        </SplitBillWrapper>
    );
};

export default memo(SplitBillWidget);
